<script lang="ts" setup>
import { t } from '@/Support/lang'
import { PlaceholderReplacements } from '@/Support/Common/Functions/PlaceholdersReplacer'
import { OhVueIcon } from 'oh-vue-icons'
import { computed } from 'vue'
import AppIcon from '@/Components/App/Global/AppIcon.vue'
import { ModelName } from '@/Support/Global/Interfaces/ModelName'

interface Props {
  title?: string
  titleReplacements?: PlaceholderReplacements | undefined
  titleRaw?: string
  enabled?: boolean
  icon?: unknown
  loadingIcon?: unknown
  iconName?: string
  loadingIconName?: string
  vertical?: boolean
  loading?: boolean
  loadingTitle?: string
  titleTranslated?: string
  iconClass?: string
  appIcon?: string | ModelName
  hero?: boolean
  heroBig?: boolean
  searchText?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  enabled: false,
  titleReplacements: undefined,
  vertical: true,
  title: 'Empty',
  loadingIcon: OhVueIcon,
  loadingIconName: 'pr-spinner',
  loadingTitle: 'Loading',
  iconClass: '',
  hero: true,
})

const clicked = () => {
  if (props.enabled) {
    emit('clicked')
  }
}

const emit = defineEmits<{
  (e: 'clicked'): void
}>()

const titleDisplayed = computed(() => {
  switch (true) {
    case props.loading:
      return t(props.loadingTitle)

    case !!props.searchText:
      return t("No results found for ':text'", {
        text: props.searchText,
      })

    case !!props.titleRaw:
      return props.titleRaw

    case !!props.titleTranslated:
      return props.titleTranslated

    default:
      return t(props.title, props.titleReplacements)
  }
})

const iconClasses = computed(() => {
  const loadingClass = props.loading
    ? 'text-neutral-400 dark:text-neutral-700 animate-spin'
    : 'text-neutral-300 dark:text-neutral-600'

  return [loadingClass, props.iconClass]
})
</script>

<template>
  <a
    class="group rounded-lg border-2 border-dashed border-neutral-300 dark:border-neutral-600 p-12 text-center hover:border-neutral-400 focus:outline-hidden focus:ring-2 focus:ring-secondary-500 focus:ring-offset-2 flex items-center justify-center"
    :class="{
      'cursor-pointer': enabled,
      'flex-col px-6 py-10': props.vertical,
      'flex-row p-6': !props.vertical,
    }"
    @click="clicked"
  >
    <AppIcon
      v-if="appIcon"
      :icon="searchText ? 'search' : appIcon"
      :hero="hero"
      :hero-big="heroBig"
      class="opacity-30 group-hover:opacity-100 mb-2"
    />
    <component
      :is="loading ? loadingIcon : icon"
      v-else-if="icon || loading"
      :name="loading ? loadingIconName : iconName"
      aria-hidden="true"
      class="w-12 h-12 text-neutral-300 dark:text-neutral-600"
      :class="iconClasses"
    />
    <span
      :class="{ 'mt-2': icon || loading }"
      class="block text-sm font-medium text-neutral-800 dark:text-neutral-200"
    >
      {{ titleDisplayed }}
    </span>
  </a>
</template>
